import React, { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/Input";
import { TextArea } from "@/components/Textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/Label";
import { useUploadMedia } from "@/lib/useUploadMedia";
import { cn, convertPriceFromCent, convertPriceToCent } from "@/lib/utils";
import {
  MenuCombo,
  MenuComboFormData,
} from "@/redux/services/menu/menu_combo.type";
import {
  useCreateMenuComboMutation,
  useUpdateMenuComboMutation,
} from "@/redux/services/menu/menu_combo.api";
import toast from "react-hot-toast";
import { logger } from "@/services/Logger";
import { ChevronDown } from "lucide-react";
import { toastApiError } from "@/lib/apiErrors";

const Field = ({ children }: { children: React.ReactNode }) => (
  <div className="[&>label]:block [&>label]:mb-1 [&>label]:text-sm [&>label]:text-card-foreground">
    {children}
  </div>
);

const Section = ({
  title,
  defaultOpen = true,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen} asChild>
      <section>
        <CollapsibleTrigger className="w-full flex items-center justify-between py-2 border-b border-border text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-card-foreground transition-colors">
          <span>{title}</span>
          <ChevronDown
            className={cn(
              "h-4 w-4 transition-transform",
              open && "rotate-180"
            )}
          />
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-4 space-y-4">
          {children}
        </CollapsibleContent>
      </section>
    </Collapsible>
  );
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  combo?: MenuCombo;
  menuId: string;
  onSuccess?: () => void;
}

const MenuComboFormDrawer: React.FC<Props> = ({
  isOpen,
  onClose,
  combo,
  menuId,
  onSuccess,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState(0);
  const [sortOrder, setSortOrder] = useState(1);
  const [isAvailable, setIsAvailable] = useState(true);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [imageFile, setImageFile] = useState<File | null>(null);

  const [createMenuCombo, { isLoading: isCreating }] =
    useCreateMenuComboMutation();
  const [updateMenuCombo, { isLoading: isUpdating }] =
    useUpdateMenuComboMutation();
  const { uploadMedia } = useUploadMedia();

  const isEditing = !!combo;
  const isLoading = isCreating || isUpdating;

  useEffect(() => {
    if (combo) {
      setName(combo.name);
      setDescription(combo.description ?? "");
      setPrice(convertPriceFromCent(combo.price));
      setSortOrder(combo.sortOrder);
      setIsAvailable(combo.isAvailable);
      setImagePreview(combo.image?.url ?? "");
      setImageFile(null);
    } else {
      setName("");
      setDescription("");
      setPrice(0);
      setSortOrder(1);
      setIsAvailable(true);
      setImagePreview("");
      setImageFile(null);
    }
  }, [combo, isOpen]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const validateForm = (): boolean => {
    if (!name.trim()) {
      toast.error("Le nom du combo est requis");
      return false;
    }
    if (price <= 0) {
      toast.error("Le prix doit être supérieur à zéro");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const priceInCents = convertPriceToCent(price);

      let imagePath = combo?.image
        ? `/api/media_objects/${combo.image.id}`
        : undefined;

      if (imageFile) {
        const uploadedMedia = await uploadMedia(imageFile);
        if (uploadedMedia) {
          imagePath = `/api/media_objects/${uploadedMedia.id}`;
        }
      }

      const comboData: MenuComboFormData = {
        name,
        description,
        price: priceInCents,
        sortOrder,
        isAvailable,
        menu: `/api/menus/${menuId}`,
        ...(imagePath && { image: imagePath }),
      };

      if (isEditing && combo) {
        await updateMenuCombo({ id: combo.id, data: comboData }).unwrap();
        toast.success("Combo mis à jour avec succès");
      } else {
        await createMenuCombo(comboData).unwrap();
        toast.success("Combo créé avec succès");
      }

      onSuccess?.();
      onClose();
    } catch (error: any) {
      logger.error("MenuComboFormDrawer", "Error saving menu combo:", error);
      toastApiError(error, "Erreur lors de l'enregistrement du combo");
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={(next) => !next && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 flex flex-col gap-0"
      >
        <form
          onSubmit={handleSubmit}
          className="flex flex-col h-full min-h-0"
        >
          <header className="flex-shrink-0 px-6 py-4 border-b border-border bg-card">
            <SheetTitle className="text-base">
              {isEditing ? "Modifier le combo" : "Créer un combo"}
            </SheetTitle>
            <SheetDescription hidden>
              Formulaire de création ou modification d'un combo.
            </SheetDescription>
          </header>

          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-6">
            <Section title="Informations">
              <Field>
                <Label label="Image" htmlFor="combo-image" />
                {imagePreview && (
                  <img
                    src={imagePreview}
                    alt="Aperçu du combo"
                    className="w-20 h-20 object-cover rounded mt-1 mb-2"
                  />
                )}
                <input
                  type="file"
                  id="combo-image"
                  className="block w-full text-sm border bg-white rounded-lg py-2 px-2 mt-1"
                  onChange={handleImageChange}
                  accept="image/*"
                />
              </Field>
              <Field>
                <Input
                  label="Nom"
                  type="text"
                  id="combo-name"
                  placeholder="Nom du combo"
                  required
                  className="h-10 py-2 text-sm"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Field>
              <Field>
                <TextArea
                  label="Description"
                  placeholder="Description du combo"
                  id="combo-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Field>
              <Field>
                <Input
                  label="Prix"
                  type="number"
                  id="combo-price"
                  placeholder="Prix"
                  min={0}
                  step="0.01"
                  className="h-10 py-2 text-sm"
                  value={price}
                  onChange={(e) => setPrice(parseFloat(e.target.value) || 0)}
                />
              </Field>
              <Field>
                <Input
                  label="Position"
                  type="number"
                  id="combo-sortOrder"
                  placeholder="Position"
                  min={1}
                  className="h-10 py-2 text-sm"
                  value={sortOrder}
                  onChange={(e) =>
                    setSortOrder(parseInt(e.target.value) || 1)
                  }
                />
              </Field>
              <div className="flex items-start gap-2.5 pt-1">
                <Checkbox
                  id="combo-isAvailable"
                  className="mt-0.5"
                  checked={isAvailable}
                  onCheckedChange={(checked) =>
                    setIsAvailable(checked as boolean)
                  }
                />
                <Label
                  htmlFor="combo-isAvailable"
                  label="Disponible"
                  id="combo-isAvailable"
                  className="cursor-pointer"
                />
              </div>
            </Section>
          </div>

          <footer className="flex-shrink-0 px-6 py-4 border-t border-border bg-card flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
            >
              Annuler
            </Button>
            <Button type="submit" isLoading={isLoading}>
              {isEditing ? "Mettre à jour" : "Créer"}
            </Button>
          </footer>
        </form>
      </SheetContent>
    </Sheet>
  );
};

export default MenuComboFormDrawer;
